import { useCallback, useEffect, useMemo, useState } from 'react';
import mapValues from 'lodash/mapValues';

import {
  type ConfiguredValidationRules,
  validate,
  type ValidationResult,
  type ValidationRuleError,
} from 'src/lib/validation';

import { useFreshRef } from './useFreshRef';

interface FormFields {
  [fieldName: string]: string;
}

interface FormErrors {
  [fieldName: string]: ValidationResult;
}

interface UseValidatedFormOptions {
  enableReinitialize?: boolean;
  autoValidate?: boolean;
}

interface UseValidatedFormParams {
  initialFields?: FormFields;
  rules?: { [fieldName: string]: ConfiguredValidationRules };
  options?: UseValidatedFormOptions;
}

interface UseValidatedFormReturn {
  fields: FormFields;
  errors: { [fieldName: string]: string | null };
  errorRules: { [fieldName: string]: string | null };
  validate: () => boolean;
  validateWithRules: () => { [fieldName: string]: string | null };
  update: (field: string, value: string) => void;
}

const mapErrors = (
  errors: FormErrors,
  fieldName: keyof ValidationRuleError
): { [fieldName: string]: string | null } =>
  mapValues(errors, (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return value[fieldName] || null;
  });

export const isValidErrors = (fieldErrors: { [fieldName: string]: any }): boolean =>
  !Object.values(fieldErrors).some((e) => e && e.length > 0);

/**
 * Hook that provides form validation functionality
 * @param params Object containing initial fields, validation rules, and options
 * @returns Object containing form state and validation methods
 */
export const useValidatedForm = ({
  initialFields = {},
  rules = {},
  options,
}: UseValidatedFormParams): UseValidatedFormReturn => {
  const [fields, setFields] = useState<FormFields>(initialFields);
  const [errors, setErrors] = useState<FormErrors>({});

  const rulesRef = useFreshRef<{ [fieldName: string]: ConfiguredValidationRules }>(rules);
  const fieldsRef = useFreshRef<FormFields>(fields);

  const enableReinitialize = options?.enableReinitialize;
  const autoValidate = options?.autoValidate;

  useEffect(() => {
    if (enableReinitialize) {
      setFields(initialFields);
    }
  }, [initialFields, enableReinitialize]);

  /**
   * Validate only one field
   */
  const validateSingleField = useCallback(
    (field: string, value: string): void => {
      // Run validations for one field
      const fieldRules = rulesRef.current[field];
      const fieldError = validate(fieldRules, value);

      setErrors((prev) => {
        // Do not cause re-render if both of them are nulls
        if (prev[field] === fieldError) {
          return prev;
        }
        // Rewrite errors for current field
        return { ...prev, [field]: fieldError };
      });
    },
    [rulesRef]
  );

  /**
   * Validate all fields
   *
   * @returns Validation rules errors
   */
  const validateWithRules = useCallback((): { [fieldName: string]: string | null } => {
    // Run validations for all possible fields
    const fieldErrors: FormErrors = {};
    Object.entries(rulesRef.current).forEach(([fieldName, fieldRules]) => {
      const fieldValue = fieldsRef.current[fieldName];
      fieldErrors[fieldName] = validate(fieldRules, fieldValue);
    });

    // Rewrite all errors
    setErrors(fieldErrors);

    // Respond, either form valid or not
    return mapErrors(fieldErrors, 'rule');
  }, [rulesRef, fieldsRef]);

  /**
   * Validate all fields
   *
   * @returns Are fields valid
   */
  const validateFields = useCallback((): boolean => {
    const fieldErrors = validateWithRules();
    return isValidErrors(fieldErrors);
  }, [validateWithRules]);

  /**
   * Set value for a field
   */
  const update = useCallback(
    (field: string, value: string): void => {
      // Update field value
      setFields((prev) => {
        // Do not cause re-render if both of them are equal
        if (prev[field] === value) {
          return prev;
        }

        // Run validations for current field, or clear error if "autoValidate" is disabled
        if (autoValidate) {
          validateSingleField(field, value);
        } else {
          setErrors((prev) => {
            return { ...prev, [field]: null };
          });
        }

        // Rewrite value for current field
        return {
          ...prev,
          [field]: value,
        };
      });
    },
    [validateSingleField, autoValidate]
  );

  const errorMessages = useMemo(() => mapErrors(errors, 'message'), [errors]);
  const errorRules = useMemo(() => mapErrors(errors, 'rule'), [errors]);

  return {
    fields,
    errors: errorMessages,
    errorRules,
    validate: validateFields,
    validateWithRules,
    update,
  };
};
